import { useNavigate, useLocation, Link } from "react-router-dom";
import { AppShell, Burger, Group, NavLink } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  faCircleQuestion,
  faRightFromBracket,
  faCoins,
  faEnvelope,
  faGear,
  faHouse,
} from "@fortawesome/sharp-regular-svg-icons";
import { PropsWithChildren } from "react";

import styles from "./navbar.module.scss";
import { AppealsRemainingQuickView } from "./AppealsRemainingQuickView";

export default function Navbar({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const { signOut } = useAuthenticator((context) => [context.user]);

  const handleLogoutClick = () => {
    signOut();
    navigate("/");
  };

  return (
    <div>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 300,
          breakpoint: "sm",
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
      >
        <AppShell.Header>
          <Group h="100%" px="md">
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              hiddenFrom="sm"
              size="sm"
            />
            <Burger
              opened={desktopOpened}
              onClick={toggleDesktop}
              visibleFrom="sm"
              size="sm"
            />
          </Group>
        </AppShell.Header>
        <AppShell.Navbar>
          <div className={styles.navbarLayout}>
            <div className={styles.navbarTop}>
              <div className={styles.logoContainer}>
                <a href="/">
                  <img
                    className={styles.logo}
                    src="/images/logo/logo_black_large.svg"
                    alt="Claimable"
                  />
                </a>
              </div>
              <div className={styles.buttonContainer}>
                <button
                  className={styles.startReferralButton}
                  onClick={() => navigate("/refer-patient")}
                  type="button"
                  disabled={location.pathname === "/refer-patient"}
                >
                  {"Start a referral"}
                </button>
              </div>
              <div className={styles.navbarNavlinkSection}>
                <NavLink
                  className={styles.navbarNavlink}
                  component={Link}
                  to="/"
                  label="Home"
                  leftSection={<FontAwesomeIcon icon={faHouse} />}
                />
                <NavLink
                  className={styles.navbarNavlink}
                  component={Link}
                  to="/refer-patient"
                  label="Refer patient"
                  leftSection={<FontAwesomeIcon icon={faEnvelope} />}
                />
                <NavLink
                  className={styles.navbarNavlink}
                  component={Link}
                  to="/sponsor-appeals"
                  label="Sponsor appeals"
                  leftSection={<FontAwesomeIcon icon={faCoins} />}
                />
              </div>
            </div>
            <div className={styles.navbarBottom}>
              <AppealsRemainingQuickView />
              <hr />
              <div className={styles.navbarBottomNavlink}>
                {
                  <NavLink
                    className={styles.navbarNavlink}
                    component={Link}
                    to="/settings"
                    label="Settings"
                    leftSection={<FontAwesomeIcon icon={faGear} />}
                  />
                }
                <NavLink
                  className={styles.navbarNavlink}
                  href="mailto:providers@getclaimable.com"
                  target="_blank"
                  label="Help"
                  leftSection={<FontAwesomeIcon icon={faCircleQuestion} />}
                />
                <NavLink
                  className={styles.navbarNavlink}
                  component="button"
                  onClick={handleLogoutClick}
                  label="Logout"
                  leftSection={<FontAwesomeIcon icon={faRightFromBracket} />}
                />
              </div>
            </div>
          </div>
        </AppShell.Navbar>
        <AppShell.Main>{children}</AppShell.Main>
      </AppShell>
    </div>
  );
}
